import { FamilyIds } from '@/assets/families'
import { isTravelskiExpress } from '@/config'
import {
  BookingSummary,
  OptionSummary,
} from '@/services/BookingService.interface'
import { Locale } from '@/types'

export const EUROSTAR_MANAGE_BOOKING_URLS = {
  'en-GB':
    'https://www.eurostar.com/customer-dashboard/en/get-booking?market=uk-en',
  'nl-NL':
    'https://www.eurostar.com/customer-dashboard/nl/get-booking?market=nl-nl',
} as const

export function getEurostarManageBookingUrl(locale: Locale): string {
  if (locale === 'nl-NL') return EUROSTAR_MANAGE_BOOKING_URLS['nl-NL']
  return EUROSTAR_MANAGE_BOOKING_URLS['en-GB']
}

export const EUROSTAR_PREST_ID_UK = 917808
export const EUROSTAR_PREST_ID_NL = 921613

export const EUROSTAR_PREST_IDS = {
  'en-GB': EUROSTAR_PREST_ID_UK,
  'nl-NL': EUROSTAR_PREST_ID_NL,
} as const

export function getEurostarPrestId(locale: Locale): number | null {
  if (locale === 'nl-NL') return EUROSTAR_PREST_IDS['nl-NL']
  if (locale === 'en-GB') return EUROSTAR_PREST_IDS['en-GB']
  return null
}

/** UK TNE night-train packs (not Eurostar PFTT / PFPTT). */
export function isUkNightTrainPackage(packageCode?: string): boolean {
  const base = String(packageCode || '').split('_')[0].trim().toUpperCase()
  return base.endsWith('TTUK')
}

export interface EurostarDisplay {
  booking: BookingSummary
  reference: string
}

export function isTrainOption(option: OptionSummary): boolean {
  return (
    option.familyId === FamilyIds.TRAIN ||
    option.familyId === FamilyIds.TRAIN_ALTERNATIVE
  )
}

export function isEurostarOption(option: OptionSummary, locale: Locale): boolean {
  const prestId = getEurostarPrestId(locale)
  return prestId !== null && isTrainOption(option) && option.prestId === prestId
}

export function getEurostarReference(
  booking: BookingSummary,
  locale: Locale
): string | null {
  const eurostarOption = booking.options?.find(
    option => isEurostarOption(option, locale) && (option.quantity ?? 0) > 0
  )
  if (!eurostarOption || !Array.isArray(eurostarOption.tripInformation)) {
    return null
  }

  const tripInfo = eurostarOption.tripInformation[0]
  const providerRef = Array.isArray(tripInfo?.providerReference)
    ? tripInfo.providerReference.find(
        (ref: unknown) => typeof ref === 'string' && ref.trim().length > 0
      )
    : null

  return typeof providerRef === 'string' ? providerRef.trim() : null
}

export function getEurostarDisplayForBookings(
  bookings: BookingSummary[],
  locale: Locale
): EurostarDisplay | null {
  if (locale !== 'en-GB' && locale !== 'nl-NL') return null

  for (const booking of bookings) {
    if (isTravelskiExpress(booking.liheId, booking.liheName, booking.packageCode)) {
      continue
    }
    // UK night-train TNE (PFTTUK / PFPTTUK) is not Eurostar, even if a train line
    // still carries generic prest 917808 from the PFPTT twin.
    // NL TNE is excluded by prest: only 921613 is Eurostar NL.
    if (isUkNightTrainPackage(booking.packageCode)) {
      continue
    }

    const reference = getEurostarReference(booking, locale)
    if (reference) return { booking, reference }
  }

  return null
}
